/**
 * The words on the QA home (docs/ux/qa.md): the review states in the tabs'
 * own vocabulary, the open-work sentence, the sub-states beside the chip
 * and the empty-queue copy. Tested; one file so the screen and the cards
 * say the same thing.
 */

import type { JobState, QueueRow, SortReviewState, WorkspaceCounts } from '@/app/types';
import { longDate, pluralise } from '@/lib/format';
import type { QueueId, QueueTab } from './useQaQueues';

export const SORT_STATE_WORD: Record<SortReviewState, string> = { review_required: 'Review required', in_review: 'In review', escalated: 'Escalated', complete: 'Complete' };
export const LOI_STATE_WORD: Record<NonNullable<QueueRow['loi']>['state'], string> = {
  machine_extracted: 'Machine extracted', review_required: 'Review required', in_review: 'In review', unresolved: 'Unresolved', approved: 'Approved',
};
export const EXTRACT_WORD: Record<JobState, string> = { queued: 'Queued', running: 'Running', succeeded: 'Done', failed: 'Failed' };

/** "2 suggestions, 1 question, 3 empty"; zeros omitted; "Nothing open"; "Not started" without a document. */
export function openWork(ws: WorkspaceCounts | null): { text: string; muted: boolean } {
  if (!ws) return { text: 'Not started', muted: true };
  const parts: string[] = [];
  if (ws.openSuggestions > 0) parts.push(pluralise(ws.openSuggestions, 'suggestion'));
  if (ws.openClarifications > 0) parts.push(pluralise(ws.openClarifications, 'question'));
  if (ws.unanswered > 0) parts.push(`${ws.unanswered} empty`);
  return parts.length > 0 ? { text: parts.join(', '), muted: false } : { text: 'Nothing open', muted: true };
}

export function sortReviewLine(s: NonNullable<QueueRow['sort']>): { word: string; detail: string } {
  const word = SORT_STATE_WORD[s.state];
  const detail = s.state === 'in_review' && s.startedAt ? `in review since ${longDate(s.startedAt)}` : s.importedAt ? `imported ${longDate(s.importedAt)}` : '';
  return { word, detail };
}

export function loiReviewLine(l: NonNullable<QueueRow['loi']>): { word: string; detail: string } {
  if (!l.extracted) return { word: 'Letter on file', detail: 'not extracted' };
  return { word: LOI_STATE_WORD[l.state], detail: l.extractedAt ? `extracted ${longDate(l.extractedAt)}` : '' };
}

export function extractWord(state: JobState | null): string {
  return state ? EXTRACT_WORD[state] : 'Not run';
}

/** The chip's sub-state: whose turn, or a resubmission (docs/ux/qa.md section 2). */
/** The table's "Waiting on" column: whose turn a Report QA row is, with the resubmission called out. */
export function turnWords(row: QueueRow): string {
  if (row.status === 8) return 'The doctor';
  if (row.status === 7 && row.round >= 2) return `QA, round ${row.round}`;
  if (row.status === 7) return 'QA';
  return row.waitingOn === 'doctor' ? 'The doctor' : row.waitingOn === 'qa' ? 'QA' : '';
}

/** The phone card's sub-state under the chip (the table says it in its own column). */
export function subStateFor(queue: QueueId, row: QueueRow): string | null {
  if (queue === 'report') {
    if (row.status === 8) return 'With the doctor';
    if (row.status === 7 && row.round >= 2) return `Resubmission, round ${row.round}`;
    return null;
  }
  if (queue === 'clarifications') return 'Waiting on the doctor';
  if (queue === 'bridge') return 'Awaiting transcription';
  return null;
}

/** An 8 is the doctor's turn: QA reads it. */
export function isReadOnlyForQa(queue: QueueId, row: QueueRow): boolean {
  return queue === 'report' && row.status === 8;
}

export function openLabel(queue: QueueId, row: QueueRow): { text: string; name: string } {
  return isReadOnlyForQa(queue, row) ? { text: 'View', name: `View ${row.ref}, read only for QA` } : { text: 'Open', name: `Open ${row.ref}` };
}

export const EMPTY_COPY: Record<QueueTab, { title: string; description: string }> = {
  all: { title: 'Nothing is waiting on QA.', description: 'Sort reviews, LOI reviews, reports, clarifications and bridge cases appear here as they reach QA.' },
  report: { title: 'Nothing is waiting on QA.', description: 'Reports arrive here when a doctor submits a draft (7 Awaiting QA) or QA sends suggestions (8 Awaiting Doctor Review).' },
  sort: { title: 'No sort review is open.', description: 'A casefile whose sort has flagged items appears here until QA completes the review.' },
  loi: { title: 'No LOI review is open.', description: 'A letter appears here from intake until QA approves the form.' },
  clarifications: { title: 'No questions are waiting on a doctor.', description: 'A question QA asks in the workspace appears here until the doctor replies and closes it.' },
  bridge: { title: 'Nothing is with Quality Scribe.', description: 'A case appears here from the doctor\'s audio coming in until the returned template is ingested.' },
};

export const TILE_COPY = {
  awaiting_qa: { label: 'awaiting QA now', description: 'Reports at 7 Awaiting QA. Press to show only those.' },
  with_doctor: { label: 'with the doctor', description: 'Reports at 8 Awaiting Doctor Review, read only for QA. Press to show only those.' },
  overdue: { label: 'overdue', description: 'Cases in these four queues past their allowance. Press to show only those in the current queue.' },
  due48: { label: 'due within 48 hours', description: 'Report QA and Clarifications rows due within 48 hours. Press to show only those in the current queue.' },
  past_mark: { label: 'past the 15-day mark', description: 'Cases attended more than 15 days ago with the report not yet released (Beam\'s report mark). Press to show only those in the current queue.' },
} as const;

/** The Bridge queue's step word and the days since the audio came in (docs/ux/bridge.md). */
export const BRIDGE_STEP_WORD: Record<string, string> = { audio_in: 'Audio in', packaged: 'Packaged', returned: 'Returned', ingested: 'Ingested', none: '' };

export function daysSince(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000));
}
